import { useState, useEffect, Dispatch, SetStateAction } from "react";
import {
  Input,
  InputProps,
  Button,
  FormField,
  SpaceBetween,
  Form,
  Container,
  Select,
  Box,
  ColumnLayout,
} from "@cloudscape-design/components-themed";
import { useAppDispatch, useAppSelector } from "../../app/hook";

import {
  createFormErrTxt,
  clearFormErrTxt,
  createFormFieldErrTxts,
  clearAllErrTxts,
  clearFormFieldErrTxt,
} from "../../features/error/validation-slice";
import {
  COMMON_LABELS,
  getDivisionDict,
  getDivisionLabel,
} from "../default-constants";
import {
  LABELS,
  DISPLAY_NAME,
  EMAIL,
  COMPANY_NAME,
  statusData,
  getCompanyTypeLabel,
} from "./constants";
import { useGetRolesQuery, useGetAuthQuery } from "../../features/api";
import { UsersSearchRequest, CSDict } from "../../features/api-interfaces";
import {
  INITIAL_SEARCH_CRITERIA,
  setSearchCriteria,
} from "../../features/slices/user-list";
import {
  getErrorText,
  isMailCharValid,
  isLengthValid,
} from "../../utils/inputValidation";
import { TypeData } from "../../features/slices/type-data";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { USER_TYPE } from "../../configs/constants";
import { useCodelistHook } from "../../app/useCodelistHook";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { omit } from "lodash";

interface ISearchBar {
  setIsSkipSearch: Dispatch<SetStateAction<boolean>>;
}

/**
 * ユーザ一覧 検索エリア
 */
const SearchBar = (props: ISearchBar) => {
  const { setIsSkipSearch } = props;
  const { searchCriteria } = useAppSelector((state) => state.userList);
  // ユーザ種別
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const { data: userInfo } = useGetAuthQuery();
  //　事業者種別コード値を取得する
  const { companyType } = useCodelistHook();
  const dispatch = useAppDispatch();

  const [typeData, setTypeData] = useState<TypeData[]>([]);
  // 権限一覧
  const [permissionList, setPermissionList] = useState<any[]>([]);
  const { formErrTxt, formFieldErrTxts } = useAppSelector(
    (state) => state.validation
  );

  // 事業者種別に紐づくロール情報取得API呼び出し
  const { data: rolesRowData } = useGetRolesQuery();

  // 検索条件
  const [searchInput, setSearchInput] = useState(INITIAL_SEARCH_CRITERIA);

  useEffect(() => {
    setSearchInput(searchCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCriteria]);

  /**
   * ユーザ種別と権限のリストを作成
   */
  useEffect(() => {
    if (rolesRowData && companyType) {
      let typeData: TypeData[] = [
        { label: COMMON_LABELS.HYPHEN, value: "", permissions: [] },
      ];
      const tmpType = companyType
        .filter((code: CSDict) => code.value !== "05")
        .map((code: CSDict) => {
          const companyTypeRole =
            rolesRowData[
              rolesRowData.findIndex((i) => i.companyTypeCode === code.value)
            ];
          return {
            label: code.label,
            value: code.value,
            permissions: [
              { label: COMMON_LABELS.HYPHEN, value: "" },
              ...companyTypeRole.roles,
            ],
          };
        });
      typeData = [...typeData, ...tmpType];

      setTypeData(typeData);
    }
  }, [rolesRowData, companyType]);

  useEffect(() => {
    const typeCode = isDocomoUser
      ? searchInput.typeCode
      : userInfo?.companyType;
    const permissionList = typeData.find(
      (type) => type.value === typeCode
    )?.permissions;
    setPermissionList(permissionList || []);
  }, [isDocomoUser, searchInput.typeCode, userInfo, typeData]);

  /**
   * アンマウント時にエラー、検索条件全クリア
   */
  useEffect(() => {
    return () => {
      dispatch(clearAllErrTxts());
    };
  }, [dispatch]);

  // 検索条件更新関数
  const handleSearchInputOnChange = (
    name: keyof UsersSearchRequest,
    value: string
  ) => {
    //入力チェック
    switch (name) {
      case "email":
        if (!isMailCharValid(value))
          dispatch(
            createFormFieldErrTxts({ name, msgId: "KGRERR025", msgArgs: [] })
          );
        else dispatch(clearFormFieldErrTxt(name));
        break;
      case "companyName":
        if (!isLengthValid(value, 40))
          dispatch(
            createFormFieldErrTxts({
              name,
              msgId: "KGRERR024",
              msgArgs: [getCompanyTypeLabel(searchInput.typeCode), "40"],
            })
          );
        else dispatch(clearFormFieldErrTxt(name));
        break;
    }

    //関連表示項目の変更　→　事業者種別が変更されたらロールもリセットされる
    if (name === "typeCode") {
      setSearchInput({ ...searchInput, [name]: value, permissionCode: "" });
      return;
    }

    setSearchInput({ ...searchInput, [name]: value });
  };

  /**
   * クリアボタンを押したときの処理.
   */
  const onClear = () => {
    setIsSkipSearch(true);
    setSearchInput(INITIAL_SEARCH_CRITERIA);
    setPermissionList([]);
    dispatch(clearAllErrTxts());
  };

  /**
   * 検索ボタンを押したときの処理.
   * @param {CustomEvent<ClickDetail>} e
   */
  const handleOnSubmit = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    const hasOne = Object.values(omit(searchInput, ["timestamp"])).some(
      (value) => value
    );
    if (!hasOne) {
      dispatch(
        createFormErrTxt({
          msgId: "KGRERR005",
          msgArgs: [],
        })
      );
      return;
    }

    if (formFieldErrTxts.length > 0) {
      return;
    }
    setIsSkipSearch(false);
    dispatch(
      setSearchCriteria({
        ...searchInput,
        timestamp: new Date().getTime(),
      })
    );
    dispatch(clearFormErrTxt());
  };

  if (!typeData) return <></>;

  return (
    <Form
      actions={
        <Button variant="normal" onClick={handleOnSubmit}>
          {COMMON_LABELS.BTNS.SEARCH}
        </Button>
      }
      errorText={formErrTxt}
    >
      <Container
        header={
          <ColumnLayout>
            <Box float="right">
              <Button variant="normal" onClick={onClear}>
                {COMMON_LABELS.BTNS.CLEAR}
              </Button>
            </Box>
          </ColumnLayout>
        }
      >
        <SpaceBetween direction="horizontal" size="l">
          {isDocomoUser && (
            <FormField
              label={LABELS.COMMON.TYPE}
              errorText={getErrorText("typeCode", formFieldErrTxts)}
            >
              <Select
                onChange={({ detail }: { detail: any }) => {
                  const value = detail.selectedOption.value;
                  handleSearchInputOnChange("typeCode", value);
                }}
                selectedOption={getDivisionDict(typeData, searchInput.typeCode)}
                options={typeData}
                empty={COMMON_LABELS.CSD.EMPTY_OPTION}
              />
            </FormField>
          )}
          {isDocomoUser && !["01", ""].includes(searchInput.typeCode) && (
            <FormField
              label={getCompanyTypeLabel(searchInput.typeCode)}
              errorText={getErrorText("companyName", formFieldErrTxts)}
            >
              <Input
                name={COMPANY_NAME}
                value={searchInput.companyName}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleSearchInputOnChange("companyName", detail.value)
                }
              />
            </FormField>
          )}
          <FormField
            label={LABELS.COMMON.EMAIL}
            errorText={getErrorText("email", formFieldErrTxts)}
          >
            <Input
              name={EMAIL}
              value={searchInput.email}
              onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                handleSearchInputOnChange(EMAIL, detail.value)
              }
            />
          </FormField>

          <FormField
            label={LABELS.COMMON.DISPLAY_NAME}
            constraintText={LABELS.COMMON.USER_NAME_CONSTRAINT_TEXT}
          >
            <Input
              name={DISPLAY_NAME}
              value={searchInput.name}
              onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                handleSearchInputOnChange(DISPLAY_NAME, detail.value)
              }
            />
          </FormField>
          <FormField
            label={LABELS.COMMON.STATUS}
            errorText={getErrorText("statusCode", formFieldErrTxts)}
          >
            <Select
              selectedOption={{
                value: searchInput.deactivateFlag,
                label: getDivisionLabel(statusData, searchInput.deactivateFlag),
              }}
              onChange={({ detail }: { detail: any }) => {
                handleSearchInputOnChange(
                  "deactivateFlag",
                  detail.selectedOption.value
                );
              }}
              options={statusData}
              selectedAriaLabel="Selected"
            />
          </FormField>

          <FormField label={LABELS.COMMON.MEMO}>
            <Input
              name="memo"
              value={searchInput.memo}
              onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                handleSearchInputOnChange("memo", detail.value)
              }
            />
          </FormField>
          {isDocomoUser && (
            <FormField label={LABELS.COMMON.COMMENT}>
              <Input
                name="comment"
                value={searchInput.comment}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleSearchInputOnChange("comment", detail.value)
                }
              />
            </FormField>
          )}
          <FormField
            label={LABELS.COMMON.PERMISSION}
            errorText={getErrorText("permissionCode", formFieldErrTxts)}
          >
            <Select
              onChange={({ detail }: { detail: any }) =>
                handleSearchInputOnChange(
                  "permissionCode",
                  detail.selectedOption.value
                )
              }
              selectedOption={getDivisionDict(
                permissionList,
                searchInput.permissionCode
              )}
              options={permissionList}
              empty={COMMON_LABELS.CSD.EMPTY_OPTION}
            />
          </FormField>
        </SpaceBetween>
      </Container>
    </Form>
  );
};

export default SearchBar;
